import { useContext, useEffect, useState } from "react";
import { ThemeWrapper } from "@verity/components";
import { CorePaper } from "@verity/components/core";
import { isChromeOsDevice } from "../../../helpers/device";
import apiClient from "../../../helpers/api-client";
import { convertPortalContextToLmsName } from "../../shared";
import { PortalControllerContext } from "../../context";
import { QuizWithProctoringOptions } from "../assessments-page/assessments-page-helper";
import VisualizerBanner from "../visualizer-banner";
import useStartAssessment from "../../start-assessment";
import ExtensionSetupInstructions from "../../../setup/components/ExtensionSetupInstructions";
import { useLaunchLockdownModal } from "../launch-lockdown-modal";
import styles from "./index.module.css";

function GeneralExtensionSetupInstructions() {
	const { portalContext, userPlatformInfo, instituteSettings, navigateToPage } = useContext(PortalControllerContext);

	return (
		<ExtensionSetupInstructions
			lms={convertPortalContextToLmsName(portalContext)}
			platformInfo={userPlatformInfo}
			managedGuestSessionRequireIncognito={instituteSettings.managedGuestSessionRequireIncognito}
			goToAssessments={() => navigateToPage("assessments")}
			courseId={portalContext.courseId}
		/>
	);
}

function AssessmentExtensionSetupInstructions({ assessment }: { assessment: QuizWithProctoringOptions }) {
	const { portalContext, userPlatformInfo, instituteSettings, navigateToPage } = useContext(PortalControllerContext);
	const { modal: launchLockdownModal, setLaunchInfo: setLockdownLaunchInfo } = useLaunchLockdownModal();
	const startAssessment: (() => void) | undefined = useStartAssessment(
		assessment,
		() => undefined,
		setLockdownLaunchInfo,
		"_top",
	);
	const { proctoringOptions } = assessment;
	const useExtensionForLockdown = isChromeOsDevice();

	return (
		<>
			<ExtensionSetupInstructions
				lms={convertPortalContextToLmsName(portalContext)}
				requireExtension={
					!!proctoringOptions?.isExtension || (!!proctoringOptions?.isLockdown && useExtensionForLockdown)
				}
				platformInfo={userPlatformInfo}
				requireManagedGuestSession={useExtensionForLockdown && !!proctoringOptions?.isLockdown}
				checkIncognitoMode={
					(!!proctoringOptions?.isExtension || (!!proctoringOptions?.isLockdown && useExtensionForLockdown)) &&
					!!proctoringOptions?.requiresIncognito
				}
				managedGuestSessionRequireIncognito={instituteSettings.managedGuestSessionRequireIncognito}
				startAssessment={startAssessment}
				goToAssessments={startAssessment ? undefined : () => navigateToPage("assessments")}
				courseId={portalContext.courseId}
			/>
			{launchLockdownModal}
		</>
	);
}

export default function ExtensionSetupPage() {
	const { portalContext, returnToAssessmentInfo } = useContext(PortalControllerContext);

	const [assessment, setAssessment] = useState<QuizWithProctoringOptions | undefined>();
	useEffect(() => {
		const { instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;
		const assessmentInfo = returnToAssessmentInfo?.assessmentInfo;
		const lms = assessmentInfo?.lms ?? portalContext.lms;
		const hostname = assessmentInfo?.hostname ?? instituteIntegrationHostname;
		const courseId = assessmentInfo?.courseId ?? lmsProvidedCourseId;
		const quizId = assessmentInfo?.quizId;
		if (lms && hostname && courseId && quizId) {
			const verifiedAssessmentInfo = {
				lms,
				hostname,
				courseId,
				quizId,
			};
			void (async () => {
				const [assessmentResp, proctoringConfiguration] = await Promise.all([
					apiClient.assessment.getAssessmentByAssessmentInfo(verifiedAssessmentInfo),

					apiClient.proctoringConfiguration.get(verifiedAssessmentInfo),
				]);
				const proctoringOptions = proctoringConfiguration?.proctoringOptionProfile.proctoringOption;
				if (assessmentResp) {
					setAssessment({
						...assessmentResp,
						hasDecoySite: false,
						proctoringOptions: {
							...proctoringConfiguration,
							requiresIncognito:
								proctoringOptions?.recordWebTraffic ||
								proctoringOptions?.disableNewTab ||
								proctoringOptions?.closeOpenTabs,
							isExtension: proctoringOptions?.isExtension,
							isLockdown: proctoringOptions?.isLockdown,
							isMobile: proctoringOptions?.isMobile,
						},
					});
				}
			})();
		}
	}, [portalContext, returnToAssessmentInfo]);

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.page }}>
			<div className={styles.visualizerBanner}>
				<VisualizerBanner />
			</div>
			<CorePaper shadowStyle="wide" className={styles.contentWrapper}>
				{assessment ? (
					<AssessmentExtensionSetupInstructions assessment={assessment} />
				) : (
					<GeneralExtensionSetupInstructions />
				)}
			</CorePaper>
		</ThemeWrapper>
	);
}
